import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllQuoteHistory } from "../../../api/client";
import { buildQuoteHistoryFilters, sortQuotesByRfqId } from "./quoteHistory";
import { queryKeys } from "../../../api/queryKeys";

interface UseQuoteHistoryOptions {
  /** Display order of the quotes, by RFQ id. */
  order: "asc" | "desc";
  /** Per-caller query settings; the query key and function are shared. */
  query?: {
    staleTime?: number;
    gcTime?: number;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
    enabled?: boolean;
  };
}

/**
 * The quote-history filters, the query they drive, and its quotes in display
 * order. Shared by the Quote History page and the RFQ list's sidebar, which
 * use one query key, so they share one cache entry per filter set.
 */
export function useQuoteHistory({ order, query }: UseQuoteHistoryOptions) {
  const [customerEmail, setCustomerEmail] = useState("");
  const [rfqId, setRfqId] = useState<number | "">("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [keyword, setKeyword] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.quoteHistory(customerEmail, rfqId, startDate, endDate, keyword),
    queryFn: () => getAllQuoteHistory(buildQuoteHistoryFilters({ customerEmail, rfqId, startDate, endDate, keyword })),
    ...query,
  });

  const quotes = useMemo(() => sortQuotesByRfqId(data?.history, order), [data, order]);

  const clearFilters = () => {
    setCustomerEmail("");
    setRfqId("");
    setStartDate(null);
    setEndDate(null);
    setKeyword("");
  };

  return {
    customerEmail,
    setCustomerEmail,
    rfqId,
    setRfqId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    keyword,
    setKeyword,
    clearFilters,
    data,
    isLoading,
    error,
    refetch,
    quotes,
  };
}
